import React, { useEffect, useMemo } from 'react';
import { Button, Text, Flex, Box, Tooltip, Group } from '@mantine/core';
import { PageZoomControls, When } from '../common';
import { useAuth } from '../../context/AuthProvider';
import { useSurveyStore } from '../../stores/SurveyStore';
import { useSurveyPricesContext } from '../../context/SurveyPricesContext';
import { useDivisionalManagerSurveyProgressContextOptional } from '../../context/DivisionalManagerSurveyProgressContext';
import { checkUserRole, isUserAdmin } from '../../utility/userRoles';
import { UserRole } from '../../types/users';
import {
    isSurveyInDivisionalManagerReview,
    isSurveyStoreManagerEditingLocked,
    formatChampionSubmitToDivisionalManagerCopy,
} from '../../pages/price-surveys/helpers';

export type SurveyTableFooterProps = {
    onDivisionalManagerActionsReady?: (
        actions: {
            submitSurvey: () => void;
            canSubmit: boolean;
            isSubmitting: boolean;
            hasChanges: boolean;
        } | null,
    ) => void;
    showSaveProgress?: boolean;
};

export function SurveyTableFooter({
    onDivisionalManagerActionsReady,
    showSaveProgress = true,
}: SurveyTableFooterProps) {
    const { authUser } = useAuth();
    const { survey, completionPercent } = useSurveyStore();
    const {
        isSaving: smIsSaving,
        changedCount: smChangedCount,
        hasAnyChanges: smHasAnyChanges,
        saveChanges,
    } = useSurveyPricesContext();

    const divisionalManagerContext =
        useDivisionalManagerSurveyProgressContextOptional();

    const isStoreManagerUser = checkUserRole(authUser, [
        UserRole.STORE_MANAGER,
    ]);
    const isDivisionalManagerUser = checkUserRole(authUser, [
        UserRole.DIVISIONAL_MANAGER,
    ]);
    const isReviewer = isDivisionalManagerUser || isUserAdmin(authUser);
    const inDivisionalManagerReview = isSurveyInDivisionalManagerReview(survey);
    const showDivisionalManagerProposedSelling =
        isReviewer && inDivisionalManagerReview;

    const competitorCellsStatic =
        isStoreManagerUser && isSurveyStoreManagerEditingLocked(survey);

    const isSurveyChampion =
        !!authUser?.id && authUser.id === survey?.championUserId;

    const showStoreManagerNextSteps =
        isStoreManagerUser && !isSurveyStoreManagerEditingLocked(survey);

    const championUser = survey?.championUser;
    const championDisplayName = championUser
        ? [championUser.firstName, championUser.lastName]
              .filter(Boolean)
              .join(' ')
        : '';
    const championSubmitToDivisionalManagerCopy =
        formatChampionSubmitToDivisionalManagerCopy(
            championDisplayName,
            championUser?.managerStore?.storeNo,
            championUser?.managerStore?.name,
        );

    useEffect(() => {
        if (
            !onDivisionalManagerActionsReady ||
            !showDivisionalManagerProposedSelling
        )
            return () => {};
        if (divisionalManagerContext) {
            onDivisionalManagerActionsReady({
                submitSurvey: divisionalManagerContext.submitSurvey,
                canSubmit:
                    divisionalManagerContext.allItemsActioned &&
                    !divisionalManagerContext.hasChanges,
                isSubmitting: divisionalManagerContext.isSubmitting,
                hasChanges: divisionalManagerContext.hasChanges,
            });
        } else {
            onDivisionalManagerActionsReady(null);
        }
        return () => {
            onDivisionalManagerActionsReady?.(null);
        };
    }, [
        showDivisionalManagerProposedSelling,
        onDivisionalManagerActionsReady,
        divisionalManagerContext?.submitSurvey,
        divisionalManagerContext?.allItemsActioned,
        divisionalManagerContext?.hasChanges,
        divisionalManagerContext?.isSubmitting,
    ]);

    const footerState = useMemo(() => {
        if (showDivisionalManagerProposedSelling && divisionalManagerContext) {
            return {
                completionPercent: divisionalManagerContext.completionPercent,
                handleSaveProgress: divisionalManagerContext.saveProgress,
                isSaving: divisionalManagerContext.isSaving,
                changedCount: divisionalManagerContext.changedCount,
                saveDisabled:
                    !divisionalManagerContext.hasChanges ||
                    divisionalManagerContext.isSaving ||
                    divisionalManagerContext.isSubmitting,
            };
        }
        return {
            completionPercent,
            handleSaveProgress: saveChanges,
            isSaving: smIsSaving,
            changedCount: smChangedCount,
            saveDisabled:
                competitorCellsStatic || smIsSaving || !smHasAnyChanges,
        };
    }, [
        showDivisionalManagerProposedSelling,
        divisionalManagerContext,
        completionPercent,
        saveChanges,
        smIsSaving,
        smChangedCount,
        smHasAnyChanges,
        competitorCellsStatic,
    ]);

    const showCompletionProgress = !inDivisionalManagerReview;

    const showCompletionTooltip =
        showCompletionProgress &&
        isStoreManagerUser &&
        !isSurveyChampion &&
        showStoreManagerNextSteps &&
        footerState.completionPercent >= 100;

    const progressLead = (
        <>
            <Text component="span" fw={600} c="dark.9">
                {footerState.completionPercent}%
            </Text>{' '}
            items completed
        </>
    );

    return (
        <Box className="py-3 border-t border-gray-200 flex-shrink-0">
            <Flex
                justify={showCompletionProgress ? 'space-between' : 'flex-end'}
                align="center"
                gap="md"
                wrap="nowrap"
            >
                <When condition={showCompletionProgress}>
                    <Box className="flex-1 min-w-0 overflow-hidden">
                        <Text
                            size="sm"
                            c="dark.6"
                            className="whitespace-nowrap overflow-hidden text-ellipsis"
                        >
                            <When
                                condition={showCompletionTooltip}
                                fallback={progressLead}
                            >
                                <Tooltip
                                    label={
                                        championSubmitToDivisionalManagerCopy
                                    }
                                    withArrow
                                    position="top"
                                    multiline
                                    maw={280}
                                >
                                    <span className="cursor-help inline">
                                        {progressLead}
                                    </span>
                                </Tooltip>
                            </When>
                            <When
                                condition={
                                    isStoreManagerUser &&
                                    showStoreManagerNextSteps
                                }
                            >
                                <Text component="span" mx={8} c="dark.4">
                                    •
                                </Text>
                                <Text component="span" c="dark.5">
                                    <When
                                        condition={isSurveyChampion}
                                        fallback={
                                            championSubmitToDivisionalManagerCopy
                                        }
                                    >
                                        Survey is ready to submit
                                    </When>
                                </Text>
                            </When>
                        </Text>
                    </Box>
                </When>
                <Group gap="md" wrap="nowrap" className="flex-shrink-0">
                    <PageZoomControls />
                    <When condition={showSaveProgress}>
                        <Button
                            onClick={footerState.handleSaveProgress}
                            size="sm"
                            radius="xl"
                            variant="filled"
                            color="primary"
                            fullWidth={false}
                            loading={footerState.isSaving}
                            disabled={footerState.saveDisabled}
                        >
                            Save Progress
                            {footerState.changedCount > 0
                                ? ` (${footerState.changedCount})`
                                : ''}
                        </Button>
                    </When>
                </Group>
            </Flex>
        </Box>
    );
}
